/**
 * Walk visible text nodes inside `container`, build Range objects for every
 * substring that matches `query`, and register them with the CSS Custom
 * Highlight API (`CSS.highlights`).
 *
 * Each instance gets its own namespaced highlight keys so multiple find-enabled
 * lists can coexist without overwriting each other's highlights:
 *  - `find-matches-{instanceId}`  – all matches
 *  - `find-current-{instanceId}`  – current match
 *
 * Uses Unicode-aware matching offsets, so case-insensitive matching can still
 * map back to valid original text ranges (e.g. Turkish İ)
 */

import { normalizeFindText, readFindText } from './find-text';
import { findTextMatches } from './unicode-search';

export type HighlightResult = {
  totalCount: number;
  currentRange: Range | null;
};

export type HighlightMode = 'all' | 'current';

const injectedStyles = new Set<string>();

function ensureHighlightStyles(matchesKey: string, currentKey: string) {
  if (injectedStyles.has(matchesKey)) return;

  const style = document.createElement('style');
  style.textContent = [
    `::highlight(${matchesKey}) { background-color: var(--yellow-4); }`,
    `.dark ::highlight(${matchesKey}) { color: var(--background); }`,
    `::highlight(${currentKey}) { background-color: var(--orange-8); }`,
  ].join('\n');

  document.head.appendChild(style);
  injectedStyles.add(matchesKey);
}

export function highlightMatches(
  container: HTMLElement,
  query: string,
  currentItemElement: HTMLElement | null,
  caseSensitive: boolean,
  currentOccurrenceIndex?: number,
  instanceId?: string,
  options?: { highlightMode?: HighlightMode; fieldsOnly?: boolean }
): HighlightResult {
  const matchesKey = instanceId ? `find-matches-${instanceId}` : 'find-matches';
  const currentKey = instanceId ? `find-current-${instanceId}` : 'find-current';
  const highlightMode = options?.highlightMode ?? 'all';

  if (!query || !CSS.highlights) {
    clearHighlights(instanceId);
    return { totalCount: 0, currentRange: null };
  }

  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  const allRanges: Range[] = [];
  let currentRange: Range | null = null;
  let occurrenceCount = 0;
  const hasCurrentTarget =
    currentItemElement !== null && currentOccurrenceIndex !== undefined;

  if (options?.fieldsOnly) {
    for (const field of container.querySelectorAll<HTMLElement>(
      '[data-find-field]'
    )) {
      if (
        field.closest('[data-find-exclude]') ||
        field.parentElement?.closest('[data-find-field]')
      )
        continue;
      const { text, positions } = readFindText(field, true);
      const inCurrentItem =
        hasCurrentTarget && currentItemElement.contains(field);
      for (const match of findTextMatches(
        text,
        normalizeFindText(query),
        caseSensitive
      )) {
        const start = positions[match.start];
        const end = positions[match.end - 1];
        const range = document.createRange();
        range.setStart(start.node, start.offset);
        range.setEnd(end.node, end.offset + 1);
        allRanges.push(range);
        if (inCurrentItem && occurrenceCount === currentOccurrenceIndex)
          currentRange = range;
        if (inCurrentItem) occurrenceCount++;
      }
    }
  } else {
    let node: Text | null;
    while ((node = walker.nextNode() as Text | null)) {
      const text = node.textContent;
      if (!text) continue;
      if (node.parentElement?.closest('[data-find-exclude]')) continue;

      const inCurrentItem =
        hasCurrentTarget && currentItemElement.contains(node);
      const matches = findTextMatches(text, query, caseSensitive);

      for (const match of matches) {
        const range = document.createRange();
        range.setStart(node, match.start);
        range.setEnd(node, match.end);
        allRanges.push(range);

        if (inCurrentItem && occurrenceCount === currentOccurrenceIndex) {
          currentRange = range;
        }
        if (inCurrentItem) {
          occurrenceCount++;
        }
      }
    }
  }

  if (allRanges.length === 0) {
    clearHighlights(instanceId);
    return { totalCount: 0, currentRange: null };
  }

  ensureHighlightStyles(matchesKey, currentKey);

  if (highlightMode === 'all') {
    CSS.highlights.set(matchesKey, new Highlight(...allRanges));
  } else {
    CSS.highlights.delete(matchesKey);
  }

  if (currentRange) {
    CSS.highlights.set(currentKey, new Highlight(currentRange));
  } else {
    CSS.highlights.delete(currentKey);
  }

  return { totalCount: allRanges.length, currentRange };
}

export function clearHighlights(instanceId?: string): void {
  if (!CSS.highlights) return;
  const matchesKey = instanceId ? `find-matches-${instanceId}` : 'find-matches';
  const currentKey = instanceId ? `find-current-${instanceId}` : 'find-current';
  CSS.highlights.delete(matchesKey);
  CSS.highlights.delete(currentKey);
}
